/** The 7 panels of the onboarding wizard (D-148). Each one calls the API of the screen that already
 * exists for it; the wizard's own endpoints only add the library, the AI test and the site. */

import {
  type OnboardingState,
  getAgencyBrandCurrentRetrieveQueryKey,
  getAgencyProfileCurrentRetrieveQueryKey,
  getDocumentsLayoutsPreviewRetrieveQueryKey,
  getOnboardingContractsListQueryKey,
  getOnboardingProgressCurrentRetrieveQueryKey,
  useAgencyBrandCurrentLogoCreate,
  useAgencyBrandCurrentPartialUpdate,
  useAgencyBrandCurrentRetrieve,
  useAgencyProfileCurrentPartialUpdate,
  useAgencyProfileCurrentRetrieve,
  useDocumentsLayoutsPreviewRetrieve,
  useOnboardingContractsCreate,
  useOnboardingContractsDestroy,
  useOnboardingContractsList,
  useOnboardingProgressAiTestCreate,
  useOnboardingProgressLibraryRetrieve,
  useOnboardingProgressPublishSiteCreate,
  useOnboardingProgressServicesCreate,
} from "@agencyos/api-client";
import { Badge, Button, Checkbox, Skeleton, TextAreaField, TextField } from "@agencyos/ui-web";
import { useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { fieldErrors } from "../../lib/settings";

type Language = "fr" | "en";

function useLanguage(): Language {
  const { i18n } = useTranslation();
  return i18n.language?.startsWith("en") ? "en" : "fr";
}

/** The wizard's state is refreshed after a change made through another screen's API. */
function useRefreshState() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: getOnboardingProgressCurrentRetrieveQueryKey() });
}

// --- 1. Legal identity ------------------------------------------------------------------------

const IDENTITY = [
  ["legal_name", "onboarding.identity.legalName"],
  ["rccm", "onboarding.identity.rccm"],
  ["nui", "onboarding.identity.nui"],
  ["phone_e164", "onboarding.identity.phone"],
  ["whatsapp_e164", "onboarding.identity.whatsapp"],
] as const;
type IdentityKey = (typeof IDENTITY)[number][0] | "address";

export function IdentityStep({ onSaved }: { onSaved: () => void }) {
  const { t } = useTranslation();
  const profile = useAgencyProfileCurrentRetrieve();
  const data = profile.data?.status === 200 ? profile.data.data : undefined;
  if (!data) return <Skeleton style={{ height: 240 }} />;
  return <IdentityForm key={data.id} initial={data} onSaved={onSaved} t={t} />;
}

function IdentityForm({
  initial,
  onSaved,
  t,
}: {
  initial: Partial<Record<IdentityKey, string | null>>;
  onSaved: () => void;
  t: (key: string) => string;
}) {
  const queryClient = useQueryClient();
  const refresh = useRefreshState();
  const [values, setValues] = useState<Record<IdentityKey, string>>(() => ({
    legal_name: initial.legal_name ?? "",
    rccm: initial.rccm ?? "",
    nui: initial.nui ?? "",
    phone_e164: initial.phone_e164 ?? "",
    whatsapp_e164: initial.whatsapp_e164 ?? "",
    address: initial.address ?? "",
  }));
  const save = useAgencyProfileCurrentPartialUpdate({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: getAgencyProfileCurrentRetrieveQueryKey() });
        void refresh();
        onSaved();
      },
    },
  });
  const errors = fieldErrors(save.error);
  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    save.mutate({ data: values });
  };
  return (
    <form className="stack" onSubmit={onSubmit}>
      <div className="onboarding__grid">
        {IDENTITY.map(([key, label]) => (
          <TextField
            key={key}
            label={t(label)}
            value={values[key]}
            error={errors[key]}
            onChange={(e) => setValues({ ...values, [key]: e.target.value })}
          />
        ))}
      </div>
      <TextAreaField
        label={t("onboarding.identity.address")}
        rows={2}
        value={values.address}
        error={errors.address}
        onChange={(e) => setValues({ ...values, address: e.target.value })}
      />
      <div>
        <Button type="submit" variant="secondary" loading={save.isPending}>
          {t("onboarding.identity.save")}
        </Button>
      </div>
    </form>
  );
}

// --- 2. Logo and colours → document header -----------------------------------------------------

export function BrandStep() {
  const { t } = useTranslation();
  const language = useLanguage();
  const queryClient = useQueryClient();
  const refresh = useRefreshState();
  const brand = useAgencyBrandCurrentRetrieve();
  const identity = brand.data?.status === 200 ? brand.data.data : undefined;
  const preview = useDocumentsLayoutsPreviewRetrieve({ language }, { query: { staleTime: 0, refetchOnMount: "always" } });
  const html = preview.data?.status === 200 ? preview.data.data.html : "";
  const changed = () => {
    void queryClient.invalidateQueries({ queryKey: getAgencyBrandCurrentRetrieveQueryKey() });
    void queryClient.invalidateQueries({ queryKey: getDocumentsLayoutsPreviewRetrieveQueryKey({ language }) });
    void refresh();
  };
  const upload = useAgencyBrandCurrentLogoCreate({ mutation: { onSuccess: changed } });
  const colors = useAgencyBrandCurrentPartialUpdate({ mutation: { onSuccess: changed } });
  const [primary, setPrimary] = useState<string | null>(null);
  const [secondary, setSecondary] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const errors = { ...fieldErrors(upload.error), ...fieldErrors(colors.error) };
  if (!identity) return <Skeleton style={{ height: 240 }} />;
  return (
    <div className="stack">
      <div className="onboarding__brand">
        <div className="logo-frame onboarding__logo">
          {identity.logo_url ? <img src={identity.logo_url} alt={t("onboarding.brand.logo")} /> : <p className="muted">{t("onboarding.brand.noLogo")}</p>}
        </div>
        <div className="stack">
          <input
            ref={input}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/svg+xml"
            hidden
            aria-label={t("onboarding.brand.chooseLogo")}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) upload.mutate({ data: { logo: file } });
            }}
          />
          <Button variant="secondary" loading={upload.isPending} onClick={() => input.current?.click()}>
            {t("onboarding.brand.chooseLogo")}
          </Button>
          {errors.logo ? (
            <p role="alert" className="ko">
              {errors.logo}
            </p>
          ) : null}
          <div className="onboarding__grid">
            <TextField
              type="color"
              label={t("onboarding.brand.primary")}
              value={primary ?? identity.primary_color ?? "#0047AB"}
              error={errors.primary_color}
              onChange={(e) => setPrimary(e.target.value)}
            />
            <TextField
              type="color"
              label={t("onboarding.brand.secondary")}
              value={secondary ?? identity.secondary_color ?? "#C0392B"}
              error={errors.secondary_color}
              onChange={(e) => setSecondary(e.target.value)}
            />
          </div>
          <div>
            <Button
              variant="plain"
              loading={colors.isPending}
              disabled={primary === null && secondary === null}
              onClick={() =>
                colors.mutate({
                  data: { ...(primary ? { primary_color: primary } : {}), ...(secondary ? { secondary_color: secondary } : {}) },
                })
              }
            >
              {t("onboarding.brand.saveColors")}
            </Button>
          </div>
        </div>
      </div>
      <h3>{t("onboarding.brand.preview")}</h3>
      {html ? (
        <iframe className="doc-preview onboarding__preview" title={t("onboarding.brand.preview")} sandbox="" srcDoc={html} data-testid="header-preview" />
      ) : (
        <Skeleton style={{ height: 200 }} />
      )}
    </div>
  );
}

// --- 3. Services from the template library -----------------------------------------------------

export function ServicesStep({ state }: { state: OnboardingState }) {
  const { t } = useTranslation();
  const language = useLanguage();
  const queryClient = useQueryClient();
  const library = useOnboardingProgressLibraryRetrieve({ language });
  const data = library.data?.status === 200 ? library.data.data : undefined;
  const [chosen, setChosen] = useState<string[]>([]);
  const apply = useOnboardingProgressServicesCreate({
    mutation: {
      onSuccess: (response) => {
        if (response.status !== 200) return;
        queryClient.setQueryData(getOnboardingProgressCurrentRetrieveQueryKey(), { ...response, status: 200, data: response.data.state });
        void library.refetch();
        setChosen([]);
      },
    },
  });
  const result = apply.data?.status === 200 ? apply.data.data : undefined;
  if (!data) return <Skeleton style={{ height: 240 }} />;
  return (
    <div className="stack">
      <h3>{t("onboarding.services.library", { version: data.version })}</h3>
      <ul className="onboarding__library">
        {data.templates.map((item) => (
          <li key={item.key}>
            <Checkbox
              label={<strong>{item.name}</strong>}
              hint={
                <>
                  {item.description}
                  <br />
                  {t("onboarding.services.packages", { list: item.packages.join(", ") })}
                </>
              }
              checked={item.added || chosen.includes(item.key)}
              disabled={item.added}
              onChange={(e) => setChosen(e.target.checked ? [...chosen, item.key] : chosen.filter((k) => k !== item.key))}
            />
            {item.added ? <Badge tone="success">{t("onboarding.services.added")}</Badge> : null}
          </li>
        ))}
      </ul>
      <div className="row">
        <Button variant="secondary" disabled={!chosen.length} loading={apply.isPending} onClick={() => apply.mutate({ data: { templates: chosen } })}>
          {t("onboarding.services.apply")}
        </Button>
        <Link className="onboarding__link" to="/catalog">
          {t("onboarding.services.openCatalog")}
        </Link>
      </div>
      {result ? <p className="ok">{t("onboarding.services.result", { services: result.services, packages: result.packages })}</p> : null}
      {state.prices_to_fill ? <p className="onboarding__missing">{t("onboarding.services.pricesWarning", { count: state.prices_to_fill })}</p> : null}
    </div>
  );
}

// --- 4. Existing contracts -----------------------------------------------------------------------

export function ContractsStep() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const refresh = useRefreshState();
  const list = useOnboardingContractsList();
  const contracts = list.data?.status === 200 ? list.data.data : [];
  const changed = () => {
    void queryClient.invalidateQueries({ queryKey: getOnboardingContractsListQueryKey() });
    void refresh();
  };
  const upload = useOnboardingContractsCreate({ mutation: { onSuccess: changed } });
  const remove = useOnboardingContractsDestroy({ mutation: { onSuccess: changed } });
  const input = useRef<HTMLInputElement>(null);
  const error = fieldErrors(upload.error).file;
  return (
    <div className="stack">
      <input
        ref={input}
        type="file"
        hidden
        aria-label={t("onboarding.contracts.choose")}
        accept=".pdf,.doc,.docx,.odt,.rtf,.txt,.jpg,.jpeg,.png"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) upload.mutate({ data: { file } });
          e.target.value = "";
        }}
      />
      <div>
        <Button variant="secondary" loading={upload.isPending} onClick={() => input.current?.click()}>
          {t("onboarding.contracts.choose")}
        </Button>
      </div>
      {error ? (
        <p role="alert" className="ko">
          {error}
        </p>
      ) : null}
      {contracts.length === 0 ? (
        <p className="muted">{t("onboarding.contracts.empty")}</p>
      ) : (
        <ul className="onboarding__files">
          {contracts.map((c) => (
            <li key={c.id}>
              {c.file_url ? (
                <a href={c.file_url} target="_blank" rel="noreferrer">
                  {c.title}
                </a>
              ) : (
                <span>{c.title}</span>
              )}
              <Badge tone="warning">{t("onboarding.contracts.toRework")}</Badge>
              <Button variant="plain" size="sm" onClick={() => remove.mutate({ id: c.id })}>
                {t("onboarding.contracts.remove")}
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// --- 5. Connections (Settings → Integrations, « Tester la connexion ») ---------------------------

export function ConnectionsStep({ state }: { state: OnboardingState }) {
  const { t } = useTranslation();
  return (
    <div className="stack">
      <ul className="onboarding__connections">
        {state.connections.map((c) => {
          const [tone, label] = !c.configured
            ? (["default", "notConfigured"] as const)
            : c.tested_ok === true
              ? (["success", "ok"] as const)
              : c.tested_ok === false
                ? (["danger", "failed"] as const)
                : (["warning", "untested"] as const);
          return (
            <li key={c.category}>
              <strong>{t(`onboarding.connections.category.${c.category}`)}</strong>
              {c.recommended ? <small className="muted">{t("onboarding.connections.recommended")}</small> : null}
              <Badge tone={tone} dot>
                {t(`onboarding.connections.${label}`)}
              </Badge>
              {c.message ? <small className="muted">{c.message}</small> : null}
            </li>
          );
        })}
      </ul>
      <Link className="onboarding__link" to="/settings/integrations">
        {t("onboarding.connections.open")}
      </Link>
    </div>
  );
}

// --- 6. AI test ----------------------------------------------------------------------------------

export function AiStep({ state }: { state: OnboardingState }) {
  const { t } = useTranslation();
  const language = useLanguage();
  const queryClient = useQueryClient();
  const [question, setQuestion] = useState(() => state.ai_question[language] ?? "");
  const run = useOnboardingProgressAiTestCreate({
    mutation: {
      onSuccess: (response) => {
        if (response.status === 200)
          queryClient.setQueryData(getOnboardingProgressCurrentRetrieveQueryKey(), { ...response, data: response.data.state });
      },
    },
  });
  const result = run.data?.status === 200 ? run.data.data : undefined;
  return (
    <div className="stack">
      <TextAreaField label={t("onboarding.ai.question")} rows={2} value={question} onChange={(e) => setQuestion(e.target.value)} />
      <div className="row">
        <Button variant="secondary" loading={run.isPending} onClick={() => run.mutate({ data: { language, question } })}>
          {t("onboarding.ai.run")}
        </Button>
        <Link className="onboarding__link" to="/ai-test">
          {t("onboarding.ai.openFull")}
        </Link>
      </div>
      {result ? (
        result.reply ? (
          <div className="onboarding__reply" aria-live="polite">
            <h3>{t("onboarding.ai.reply")}</h3>
            <p>{result.reply}</p>
            <small className="muted">{t("onboarding.ai.outcome", { outcome: result.outcome })}</small>
          </div>
        ) : (
          <p role="alert" className="ko">
            {t("onboarding.ai.error", { reason: result.reason || result.outcome })}
          </p>
        )
      ) : null}
    </div>
  );
}

// --- 7. Website online ---------------------------------------------------------------------------

export function WebsiteStep({ state }: { state: OnboardingState }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const publish = useOnboardingProgressPublishSiteCreate({
    mutation: {
      onSuccess: (response) => {
        if (response.status === 200)
          queryClient.setQueryData(getOnboardingProgressCurrentRetrieveQueryKey(), { ...response, data: response.data.state });
      },
    },
  });
  return (
    <div className="stack">
      {state.site_published_at ? (
        <p className="ok">
          {t("onboarding.website.online")}{" "}
          <a href={state.site_url} target="_blank" rel="noreferrer">
            {state.site_url}
          </a>
        </p>
      ) : null}
      <div className="row">
        <Button variant="secondary" loading={publish.isPending} onClick={() => publish.mutate()}>
          {t("onboarding.website.publish")}
        </Button>
        <Link className="onboarding__link" to="/website">
          {t("onboarding.website.edit")}
        </Link>
      </div>
    </div>
  );
}
